import { computed, type Ref, ref } from 'vue'
import type { LocationRoute, LocationRoutes, UpdateLocationPitcherEvent } from '@sdk/interfaces'
import type useLocationAPI from '@lib/composables/useLocationAPI'
import { isEqual } from 'lodash-es'

interface UseLocationManipulation {
  routes: LocationRoutes
  relations: { [key: string]: string }
  app: string
  actionsQueue: Ref<UpdateLocationPitcherEvent[]>
}

type LocationQuery = Record<string, any>

export interface RegisterLocation {
  child_app: string
  parent_app?: string
  path?: string
  query?: LocationQuery
}

interface Route {
  path: string
  query: LocationQuery
}

export default function useLocationManipulation({ routes, relations, app, actionsQueue }: UseLocationManipulation) {
  const isPathRestored = ref(false)
  const isAppMounted = computed(() => routes[app]?.is_mounted ?? false)
  const isAppRestored = computed(() => routes[app]?.is_restoring === false)

  const recoverRouteFromLocations = (rootApp: string): Route => {
    let childApp: string | undefined = relations[rootApp]
    let childLocation = routes[childApp]
    let newUrl = childLocation?.path ?? ''
    let lastApp: string | null = null
    let newQuery = {}

    while (childApp) {
      if (!relations[childApp]) {
        lastApp = childApp
      }

      childApp = relations[childApp]
      childLocation = routes[childApp]

      if (childLocation?.is_inactive) {
        childApp = undefined
      } else {
        newUrl += childLocation?.path ?? ''
      }
    }

    if (lastApp) {
      newQuery = routes[lastApp]?.query ?? {}
    }

    return {
      path: newUrl,
      query: newQuery,
    }
  }

  const isSameRouteAsStored = (internalRouter: ReturnType<typeof useLocationAPI>['internalRouter']) => {
    if (!routes[app]) {
      return false
    }

    return isSameRoute(
      {
        path: routes[app].path,
        query: routes[app].query,
      },
      {
        path: internalRouter.path.value,
        query: internalRouter.query.value,
      },
    )
  }

  const isSameRoute = (route1?: Route, route2?: Route) => {
    if (!route1 || !route2) return false
    const isSamePath = route1.path === route2.path
    const isSameQuery = isEqual(route1.query, route2.query)

    return isSamePath && isSameQuery
  }

  const unregisterLocation = (appName: string) => {
    const deletedRelations = deleteRelation(appName)

    for (const relation of deletedRelations) {
      delete routes[relation]
    }
  }

  const deleteRelation = (appName: string) => {
    const deletedApps: string[] = []

    for (const relation in relations) {
      if (relations[relation] === appName) {
        deletedApps.push(appName)
        delete relations[relation]
      }
    }

    let currentApp: string | null = appName

    while (currentApp) {
      if (currentApp in relations) {
        currentApp = relations[currentApp]
        deletedApps.push(currentApp)
        delete relations[currentApp]
      } else {
        currentApp = null
      }
    }

    return deletedApps
  }

  const addActionToQueue = (body: UpdateLocationPitcherEvent) => {
    actionsQueue.value.push(body)
  }

  const createRoute = (params: Partial<LocationRoute> = {}): LocationRoute => ({
    path: '',
    query: {},
    is_restoring: false,
    is_mounted: false,
    is_inactive: false,
    ...params,
  })

  const registerLocation = ({ child_app, parent_app = app, path, query }: RegisterLocation) => {
    routes[child_app] = createRoute({
      path: path ?? '',
      query: query ?? {},
    })

    const oldChild = relations[parent_app]

    relations[parent_app] = child_app

    // cleanup the old registered routes that didn't unregister itself
    if (oldChild) {
      delete routes[oldChild]
    }
  }

  return {
    isPathRestored,
    isAppMounted,
    isAppRestored,
    recoverRouteFromLocations,
    isSameRoute,
    isSameRouteAsStored,
    deleteRelation,
    addActionToQueue,
    registerLocation,
    createRoute,
    unregisterLocation,
  }
}
